/**
 * Fume web client — mobile-first, installable, Beginner or Expert.
 *
 * Beginner ('Simple') mode: plain language, fewer choices, and an explicit
 * LIVE-session framing — Fume is a live channel, not a mailbox, so both people
 * must be online at the same time. A readiness nudge sets that expectation up
 * front and a waiting room shows the live connection state so the sender is
 * never guessing. Expert mode keeps the full one-way/two-way handshake with all
 * the technical readouts (lane · seq, link state, destruct reasons, scope).
 *
 * The level is in-memory only (ui/level.tsx) and resets each load.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useConnection, type ChatMessage, type MsgStatus, type Mode } from './session/useConnection.js';
import { TtlGraph } from './components/TtlGraph.js';
import { Landing, isStandalone } from './components/Landing.js';
import { VersionTag } from './components/VersionTag.js';
import { QrCode } from './components/QrCode.js';
import { QrScanner } from './components/QrScanner.js';
import { UiLevelProvider, useUiLevel, LevelToggle } from './ui/level.js';

const REASON_LABEL: Record<string, string> = {
  manual: 'manual destruct',
  backgrounded: 'left the app too long',
  pagehide: 'page hidden / closed',
  'idle-lapse': 'idle TTL lapsed',
  'peer-closed': 'peer disconnected',
  'peer-burn': 'peer self-destructed',
};

const STATUS_LABEL: Record<MsgStatus, string> = {
  sent: 'sent',
  read: '✓ read',
  unconfirmed: 'unconfirmed',
};
const STATUS_LABEL_SIMPLE: Record<MsgStatus, string> = {
  sent: 'Sent',
  read: '✓ Read',
  unconfirmed: 'Not delivered',
};

function CodeBox(props: { title: string; hint: string; code: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(props.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard blocked; user selects manually */
    }
  };
  return (
    <div className="codebox">
      <div className="section-header">
        <h2>{props.title}</h2>
        <button type="button" className="mini" onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <p className="hint">{props.hint}</p>
      <textarea readOnly value={props.code} rows={3} onFocus={(e) => e.target.select()} />
    </div>
  );
}

function PasteForm(props: {
  title: string;
  hint: string;
  cta: string;
  onSubmit: (v: string) => void | Promise<void>;
}): JSX.Element {
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const v = value.trim();
    if (!v || submitting) return;
    setSubmitting(true);
    try {
      await props.onSubmit(v);
    } finally {
      if (mounted.current) setSubmitting(false); // stays disabled if the form unmounts on success
    }
  };
  return (
    <form className="codebox" onSubmit={(e) => void submit(e)}>
      <div className="section-header">
        <h2>{props.title}</h2>
      </div>
      <p className="hint">{props.hint}</p>
      <textarea
        value={value}
        rows={3}
        disabled={submitting}
        placeholder="Paste the code here…"
        onChange={(e) => setValue(e.target.value)}
      />
      <button type="submit" disabled={submitting || value.trim().length === 0}>
        {submitting ? (
          <span className="btn-connecting">
            <span className="spinner spinner-inline" aria-hidden="true" /> Connecting…
          </span>
        ) : (
          props.cta
        )}
      </button>
    </form>
  );
}

function SasBadge(props: { sas: string }): JSX.Element {
  const { level } = useUiLevel();
  return (
    <div className="sas">
      <span className="eyebrow">
        {level === 'beginner'
          ? 'Safety check — you should both see the same 6 digits'
          : 'Verify code — must match on both devices'}
      </span>
      <span className="sas-code">{props.sas}</span>
    </div>
  );
}

function LinkPill(props: { state: string | null }): JSX.Element {
  const { level } = useUiLevel();
  const s = props.state ?? 'new';
  const expert: Record<string, string> = {
    connected: 'peer connected',
    connecting: 'connecting…',
    failed: 'connection failed',
    closed: 'closed',
    new: 'waiting',
  };
  const simple: Record<string, string> = {
    connected: 'Connected',
    connecting: 'Connecting…',
    failed: "Couldn't connect",
    closed: 'Disconnected',
    new: 'Waiting…',
  };
  const map = level === 'beginner' ? simple : expert;
  const label = map[s] ?? (level === 'beginner' ? 'Waiting…' : 'waiting');
  return <span className={`link-pill is-${s}`}>{label}</span>;
}

function Chat(props: {
  messages: ChatMessage[];
  canCompose: boolean;
  disabled: boolean;
  emptyHint: string;
  onSend: (text: string) => void;
  ttlSeconds: number;
}): JSX.Element {
  const { level } = useUiLevel();
  const beginner = level === 'beginner';
  const [text, setText] = useState('');
  const submit = (e: FormEvent): void => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    props.onSend(t);
    setText('');
  };
  return (
    <div className="chat">
      <ol className="timeline" aria-live="polite">
        {props.messages.length === 0 && <li className="empty">{props.emptyHint}</li>}
        {props.messages.map((m) => {
          const poofed = m.dissipated === true;
          const dissipating = !poofed && m.dissipateAt !== undefined;
          return (
            <li key={m.id} className={`bubble from-${m.from}${poofed ? ' poofed' : ''}`}>
              <span className="meta">
                <span className="who">{m.from === 'me' ? 'You' : 'Peer'}</span>
                <span className="lane">
                  {beginner
                    ? m.from === 'me' && m.status
                      ? STATUS_LABEL_SIMPLE[m.status]
                      : ''
                    : `${m.lane} · seq ${m.seq}${m.from === 'me' && m.status ? ` · ${STATUS_LABEL[m.status]}` : ''}`}
                </span>
              </span>
              {poofed ? (
                <span className="poof-chip">{m.from === 'me' ? 'vanished' : '— vanished —'}</span>
              ) : (
                <span
                  className={`text${dissipating ? ' dissipating' : ''}`}
                  style={dissipating ? { animationDuration: `${props.ttlSeconds}s` } : undefined}
                >
                  {m.text}
                </span>
              )}
            </li>
          );
        })}
      </ol>
      {props.canCompose ? (
        <form className="composer" onSubmit={submit}>
          <textarea
            rows={2}
            value={text}
            disabled={props.disabled}
            placeholder={props.disabled ? 'Session destroyed' : 'Message…'}
            onChange={(e) => setText(e.target.value)}
          />
          <button type="submit" disabled={props.disabled || text.trim().length === 0}>
            Send
          </button>
        </form>
      ) : (
        <p className="readonly-note">
          {beginner
            ? 'You can read here. Your reads are confirmed back automatically.'
            : 'Read-only — this is a one-way send. Your reads are acknowledged back automatically.'}
        </p>
      )}
    </div>
  );
}

function InfoSheet(): JSX.Element {
  const { level } = useUiLevel();
  return (
    <details className="info-sheet">
      <summary>How this works &amp; what it guarantees</summary>
      <div className="info-body">
        {level === 'beginner' && (
          <p className="hint">
            Fume is a <strong>live</strong> connection between two phones — like a call, not a text.
            You both need to be online at the same time. Your messages travel straight between the
            devices, disappear as they're read, and self-destruct on a timer.
          </p>
        )}
        <p className="eyebrow">Flow</p>
        <ol className="steps">
          <li>One side starts and shares an invite — a link (via the relay) or a copy/paste code.</li>
          <li>The other opens it; the WebRTC offer/answer is exchanged (over the relay, or by copy/paste).</li>
          <li>A peer-to-peer WebRTC channel opens between the browsers; the relay drops out.</li>
          <li>Messages travel as capsules; each resets the shared TTL and is acknowledged.</li>
        </ol>
        <p className="eyebrow">Exposure model (frame 0016)</p>
        <ul className="lifecycle">
          <li><span />Delivered messages are gone at read-time (deleting ratchet)</li>
          <li><span />TTL bounds only the still-live seed</li>
          <li><span />No secret persisted; wipe is RAM-volatility + destruct</li>
        </ul>
        <p className="eyebrow">Scope</p>
        <p className="hint">
          POC. The invite carries the whole seed over one channel; a real deployment
          splits the root 2-of-2 across two independent OOB channels and derives the
          shared secret via SPAKE2 (never sent). STUN-only (no TURN), so hard-NAT
          peers may not connect.
        </p>
      </div>
    </details>
  );
}

function ShareBox(props: { title: string; hint: string; link: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(props.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard blocked; user selects manually */
    }
  };
  const share = async (): Promise<void> => {
    try {
      await navigator.share({ text: props.link });
    } catch {
      /* share sheet cancelled */
    }
  };
  return (
    <div className="codebox">
      <div className="section-header">
        <h2>{props.title}</h2>
        <div className="share-actions">
          {canShare && (
            <button type="button" className="mini" onClick={() => void share()}>
              Share
            </button>
          )}
          <button type="button" className="mini" onClick={() => void copy()}>
            {copied ? 'Copied' : 'Copy link'}
          </button>
        </div>
      </div>
      <p className="hint">{props.hint}</p>
      <textarea readOnly value={props.link} rows={3} onFocus={(e) => e.target.select()} />
    </div>
  );
}

function Connecting(props: { text: string; sub?: string }): JSX.Element {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="connecting" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <div className="connecting-text">
        <span className="connecting-main">{props.text}</span>
        <span className="connecting-sub">
          {props.sub ?? 'This can take up to ~20 seconds'} · {elapsed}s
        </span>
      </div>
    </div>
  );
}

const TTL_PRESETS: { label: string; v: number }[] = [
  { label: '10s', v: 10 },
  { label: '30s', v: 30 },
  { label: '1 min', v: 60 },
  { label: '5 min', v: 300 },
];

function TtlPicker(props: { value: number; onChange: (v: number) => void; expert: boolean }): JSX.Element {
  const isPreset = TTL_PRESETS.some((p) => p.v === props.value);
  return (
    <div className="ttl-picker">
      <span className="eyebrow">{props.expert ? 'Auto-disappear (TTL)' : 'Disappears after'}</span>
      <div className="ttl-options" role="group" aria-label="Disappear timer">
        {TTL_PRESETS.map((p) => (
          <button
            key={p.v}
            type="button"
            className={`ttl-opt${props.value === p.v ? ' is-on' : ''}`}
            aria-pressed={props.value === p.v}
            onClick={() => props.onChange(p.v)}
          >
            {p.label}
          </button>
        ))}
        {props.expert && (
          <label className="ttl-custom">
            <input
              type="number"
              min={3}
              max={3600}
              value={isPreset ? '' : props.value}
              placeholder="custom"
              onChange={(e) => {
                const n = Math.floor(Number(e.target.value));
                if (Number.isFinite(n) && n >= 3 && n <= 3600) props.onChange(n);
              }}
            />
            s
          </label>
        )}
      </div>
    </div>
  );
}

/** Parse a hosted-rendezvous join link fragment `#r=<roomId>&s=<seed>` (seed base64url)
 *  from any URL, bare fragment, or scanned-QR string. */
function parseJoinLink(input: string): { id: string; seed: string } | null {
  const hashIndex = input.indexOf('#');
  const frag = hashIndex >= 0 ? input.slice(hashIndex + 1) : input;
  const params = new URLSearchParams(frag);
  const id = params.get('r');
  const seed = params.get('s');
  return id && seed ? { id, seed } : null;
}

/** Join params carried on the current location hash, if any. */
function joinParamsFromHash(): { id: string; seed: string } | null {
  if (typeof window === 'undefined') return null;
  return parseJoinLink(window.location.hash);
}

/** 0.2 — a beginner receiver otherwise learns "read-only" only by failing to type.
 *  State up front what kind of session they are in. */
function ModeNotice(props: { mode: Mode; amSender: boolean }): JSX.Element | null {
  const { level } = useUiLevel();
  if (level !== 'beginner') return null;
  const text =
    props.mode === 'oneway'
      ? props.amSender
        ? "One-way secret: you send, they read. You get a read-receipt back — they can't reply."
        : "You're receiving a one-way secret. You can read it here — replying is off for this one."
      : 'Two-way chat: you can both send messages, live.';
  return <p className={`mode-notice is-${props.mode}`}>{text}</p>;
}

function AppInner(): JSX.Element {
  const [entered, setEntered] = useState(() => isStandalone() || joinParamsFromHash() !== null);
  const { level } = useUiLevel();
  const beginner = level === 'beginner';
  const c = useConnection();
  const [ttl, setTtl] = useState(30);
  const [showQr, setShowQr] = useState(false);
  const [scanning, setScanning] = useState(false);
  const autoJoined = useRef(false);

  useEffect(() => {
    if (autoJoined.current) return;
    const params = joinParamsFromHash();
    if (!params) return;
    autoJoined.current = true;
    // Scrub the seed out of the address bar / history before anything else.
    try {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    } catch {
      /* history unavailable */
    }
    setEntered(true);
    void c.joinViaLink(params.id, params.seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 0.1 — a share link clicked while the app is ALREADY open updates the hash but
  // does not remount, so the once-at-mount join above never re-runs (the user had to
  // close + reopen the tab). Listen for hashchange and, when a fresh join link lands,
  // reload so the mount path (entered + auto-join + seed-scrub) runs cleanly against
  // the new link. reload fires pagehide, so any live session tears down with a
  // coordinated burn; the seed-scrub clears the hash, so this never loops.
  useEffect(() => {
    const onHashChange = (): void => {
      if (joinParamsFromHash()) window.location.reload();
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  if (scanning) {
    return (
      <QrScanner
        onResult={(text) => {
          const parsed = parseJoinLink(text);
          setScanning(false);
          if (parsed) {
            setEntered(true);
            void c.joinViaLink(parsed.id, parsed.seed);
          }
        }}
        onCancel={() => setScanning(false)}
      />
    );
  }

  if (!entered) return <Landing onContinue={() => setEntered(true)} />;

  const destroyed = c.phase === 'destroyed';
  const modeLabel = c.mode === 'oneway' ? 'One-way' : 'Two-way';
  const waiting = (c.phase === 'invite-created' || c.phase === 'answer-created') && c.linkState !== 'connected';

  const statusPill = destroyed
    ? beginner
      ? 'Chat deleted'
      : `Destroyed — ${REASON_LABEL[c.destructReason ?? 'manual']}`
    : c.phase === 'connected'
      ? beginner
        ? 'Live'
        : 'Session live'
      : c.phase === 'choose'
        ? beginner
          ? 'Ready'
          : 'No session'
        : beginner
          ? 'Setting up…'
          : 'Handshaking';

  return (
    <main className="app">
      <header className="appbar">
        <h1>Fume</h1>
        <div className="appbar-right">
          <LevelToggle />
          <span className={`status-pill ${destroyed ? 'is-destroyed' : c.phase === 'connected' ? 'is-live' : ''}`}>
            {statusPill}
          </span>
        </div>
      </header>

      {c.error && <div className="error">{c.error}</div>}

      {c.phase === 'choose' && (
        <div className="choose-screen">
          <TtlPicker value={ttl} onChange={setTtl} expert={!beginner} />
          {beginner ? (
          <div className="choose">
            <button type="button" className="choice" onClick={() => void c.startSessionHosted('oneway', ttl)}>
              <span className="big">Send a one-way secret</span>
              <span className="sub">They read it live — they can't reply. You get a read-receipt back.</span>
            </button>
            <button type="button" className="choice" onClick={() => void c.startSessionHosted('twoway', ttl)}>
              <span className="big">Start a two-way chat</span>
              <span className="sub">Both of you send messages, live.</span>
            </button>
            <button type="button" className="choice secondary" onClick={c.beginJoin}>
              <span className="big">Receive a message</span>
              <span className="sub">Got an invite? Tap to connect</span>
            </button>
            <button type="button" className="choice secondary" onClick={() => setScanning(true)}>
              <span className="big">Scan a QR code</span>
              <span className="sub">In person? Scan their code to connect — no pasting.</span>
            </button>
            <p className="nudge">
              This is a live connection, like a call — make sure your friend is ready to join now,
              not later.
            </p>
          </div>
        ) : (
          <div className="choose">
            <button type="button" className="choice" onClick={() => void c.startSessionHosted('oneway', ttl)}>
              <span className="big">Send a secret</span>
              <span className="sub">One-way — you send, they read (with a read receipt back)</span>
            </button>
            <button type="button" className="choice" onClick={() => void c.startSessionHosted('twoway', ttl)}>
              <span className="big">Two-way conversation</span>
              <span className="sub">Both send — two lanes inside the shield</span>
            </button>
            <button type="button" className="choice secondary" onClick={c.beginJoin}>
              <span className="big">Join a session</span>
              <span className="sub">Paste an invite code you received</span>
            </button>
            <button type="button" className="choice secondary" onClick={() => setScanning(true)}>
              <span className="big">Scan a QR</span>
              <span className="sub">Scan a Fume link from another device</span>
            </button>
          </div>
          )}
        </div>
      )}

      {c.phase === 'invite-created' && (
        <div className="handshake">
          <div className="live-head">
            {!beginner && <span className="mode-badge">{modeLabel}</span>}
            <LinkPill state={c.linkState} />
          </div>
          {beginner && waiting && (
            <div className="waiting">
              <span className="pulse" />
              Waiting for them to join… keep this screen open.
            </div>
          )}
          {c.hosted ? (
            <>
              <ShareBox
                title={beginner ? 'Share this link' : '1 · Your invite link'}
                hint={
                  beginner
                    ? "Send it to your friend now. When they open it you'll connect automatically - no codes to paste back. Keep this screen open."
                    : 'Send this link over any channel. The seed rides the URL fragment (never sent to the relay); the relay brokers only the WebRTC signaling.'
                }
                link={c.shareLink}
              />
              <div className="qr-block">
                <button type="button" className="mini" onClick={() => setShowQr((v) => !v)}>
                  {showQr ? 'Hide QR' : 'Show QR'}
                </button>
                {showQr && (
                  <>
                    <QrCode value={c.shareLink} />
                    <p className="hint">
                      {beginner
                        ? 'In person? Have them scan this with their camera to connect — no link to send.'
                        : 'In-person zero-paste: the peer scans this to join (carries the same fragment seed).'}
                    </p>
                  </>
                )}
              </div>
              <SasBadge sas={c.sas} />
            </>
          ) : (
            <>
          <CodeBox
            title={beginner ? 'Share this code' : '1 · Your invite code'}
            hint={
              beginner
                ? "Send it to your friend now so they can join. Keep this screen open — it only works while you're both here."
                : 'Send this to the other person over any channel. It carries the session seed.'
            }
            code={c.inviteCode}
          />
          <SasBadge sas={c.sas} />
          {c.answerAccepted ? (
            <Connecting
              text={beginner ? 'Connecting…' : 'Connecting — finishing the handshake'}
              sub="Can take up to ~20s. If it stalls, your peer may be behind a strict NAT."
            />
          ) : (
          <PasteForm
            title={beginner ? 'Paste their reply' : '2 · Their answer code'}
            hint={
              beginner
                ? "They'll send a reply back. Paste it here to connect."
                : 'They\'ll paste your invite and send back an answer code. Paste it here to connect.'
            }
            cta="Connect"
            onSubmit={(v) => c.submitAnswer(v)}
          />
          )}
            </>
          )}
        </div>
      )}

      {c.phase === 'awaiting-invite' && (
        <div className="handshake">
          {c.hosted ? (
            <>
              <div className="live-head">
                <LinkPill state={c.linkState} />
              </div>
              <Connecting text={beginner ? 'Connecting to your friend...' : 'Joining the rendezvous...'} />
              {c.sas && <SasBadge sas={c.sas} />}
            </>
          ) : (
            <>
          <PasteForm
            title={beginner ? 'Paste the invite' : 'Paste the invite code'}
            hint={beginner ? 'Paste the code your friend sent you.' : 'Paste the code the other person sent you.'}
            cta={beginner ? 'Continue' : 'Generate answer'}
            onSubmit={(v) => c.joinSession(v)}
          />
            </>
          )}
        </div>
      )}

      {c.phase === 'answer-created' && (
        <div className="handshake">
          <div className="live-head">
            {!beginner && <span className="mode-badge">{modeLabel}</span>}
            <LinkPill state={c.linkState} />
          </div>
          {c.hosted ? (
            <>
              {waiting && (
                <Connecting
                  text={beginner ? 'Almost there - finishing the connection...' : 'Waiting for the peer to finalize...'}
                />
              )}
              <ModeNotice mode={c.mode} amSender={c.amSender} />
              <SasBadge sas={c.sas} />
            </>
          ) : (
            <>
          {beginner && waiting && (
            <div className="waiting">
              <span className="pulse" />
              Almost there — waiting for them to finish connecting…
            </div>
          )}
          <CodeBox
            title={beginner ? 'Send this reply back' : 'Your answer code'}
            hint={
              beginner
                ? "Send this back to the person who invited you. You'll connect once they paste it."
                : 'Send this back to the person who invited you. The channel opens once they paste it.'
            }
            code={c.answerCode}
          />
          <SasBadge sas={c.sas} />
            </>
          )}
        </div>
      )}

      {(c.phase === 'connected' || destroyed) && (
        <div className="live">
          <div className="live-head">
            {!beginner && <span className="mode-badge">{modeLabel}</span>}
            <LinkPill state={c.linkState} />
          </div>
          <SasBadge sas={c.sas} />
          <ModeNotice mode={c.mode} amSender={c.amSender} />
          {c.mode === 'twoway' && (
            <TtlGraph
              ttlSeconds={c.ttlSeconds}
              lastActivityAt={c.lastActivityAt}
              active={c.phase === 'connected' && c.lastActivityAt > 0}
              destroyed={destroyed}
              onExpire={() => c.destruct('idle-lapse')}
            />
          )}
          <Chat
            messages={c.messages}
            canCompose={c.amSender}
            disabled={destroyed}
            ttlSeconds={c.ttlSeconds}
            emptyHint={
              c.amSender
                ? beginner
                  ? 'Connected — send a message. It disappears on a timer.'
                  : 'Connected. Send a message — the TTL starts ticking.'
                : beginner
                  ? 'Connected — waiting for their message…'
                  : 'Connected. Waiting for the sender…'
            }
            onSend={c.send}
          />
          {destroyed ? (
            <button type="button" className="destruct" onClick={c.reset}>
              {beginner ? 'Start over' : 'Start a new session'}
            </button>
          ) : (
            <button type="button" className="destruct" onClick={() => c.destruct('manual')}>
              {beginner ? 'Delete this chat now' : 'Destruct session'}
            </button>
          )}
        </div>
      )}

      <InfoSheet />
      <VersionTag />
    </main>
  );
}

export function App(): JSX.Element {
  return (
    <UiLevelProvider>
      <AppInner />
    </UiLevelProvider>
  );
}
